import type { Schemas } from '@npc/api-client'

import { Card, CardTitle } from '../../components/card'
import { StatusDot } from '../../components/tag'
import { T } from '../../copy/text'
import { ago } from '../../app/format'

/**
 * 「这台机器上现在有哪几把钥匙」——**两把并排，各答三个问题。**
 *
 * ## 它与上面那一块回答的不是同一个问题
 *
 * 「万一我这边挂了，你还进得去吗」回答**能不能**，这一块回答**靠哪一把**。
 * 合成一块的话，两把钥匙的区别会被那个大问题盖掉，而这个区别正是 arron 要的。
 *
 * ## 两把性质不同，所以同一个问题不共用模板
 *
 * | | 性质 | 它用不了的时候 |
 * |---|---|---|
 * | **他给的那把** | 他原来就有的 | **他没有自己的入口了**（红） |
 * | **平台自己那把** | 接入时加上去的 | 我们管不了这台机器，**而他不受影响**（琥珀） |
 *
 * 字面很像的两句话不共用一条模板，**代价是多六条，换的是不会把一件红的事
 * 显示成琥珀的**。文案层已经按这个分好了（`yours.*` 与 `ours.*` 各一套）。
 *
 * ## ★ 契约还没有「每把各自的状态」，这里是从现有字段推的
 *
 * 三档状态（能用 / 不能用 / 说不准）与每把各自的验证时刻，
 * **`HostAccessMethod` 上目前都没有**。这里的映射写在下面每一处，
 * 一眼能看出哪个字段喂哪一句：
 *
 * ```
 * live = false                    → 两把都走 status.unknown（这次压根没去验）
 * user_keys_intact                → 他那把 usable / unusable
 * platform_key_installed          → 我们那把 usable / unusable
 * checked_at                      → 两把共用（契约上只有一个时刻）
 * ```
 *
 * **`user_keys_intact` 拿来当「他那把还灵不灵」不是凑合**：契约给它的定义
 * 就是「他自己的入口还在不在……两者有其一，他就进得去」，
 * 而 `yours.status.unusable` 那句话说的正是这件事。
 *
 * 契约把每把各自的状态分出来的那天，**改的是下面这几行，不是这一屏的结构**。
 */
export function Keys({ access }: { access: Schemas['HostAccessMethod'] }) {
  return (
    <Card>
      <CardTitle className="mb-npc-grid-sm block">
        <T id="host.access.keys.heading" />
      </CardTitle>

      <div className="grid grid-cols-2 items-start gap-npc-grid">
        <Column
          headline={
            /**
             * ★ **零那一档的判据是「退路在不在」，不是「有几把钥匙」。**
             *
             * `keys.yours_none`（「没有你自己的钥匙了，只剩我那一把」，红）
             * 那条模板的 note 写着它是给**退路不在**那一刻用的。
             * 而「一把钥匙都没有」与「退路不在」**不是一回事**：
             * 用密码接进来的机器一把钥匙都没有，**而密码登录还开着，他照样进得去**——
             * 线上 10.62.0.35 正是这一台。
             *
             * 拿 `user_key_count === 0` 当判据的话，那台机器会被显示成红的
             * 「你被关在外面了」，**而他其实好好的**。判据用 `user_keys_intact`,
             * 契约给它的定义就是「他自己的入口还在不在……两者有其一，他就进得去」。
             *
             * ★ **密码那一档没有合适的标题模板，所以这里什么都不写。**
             * `keys.yours` 是「你自己的 {count} 把」，填 0 是句病句；
             * `keys.yours_none` 是红的、说的是退路没了。两条都不对。
             * 「密码登录还开着」这件事上面那一块（`host.access.original.password`）
             * 已经在说了，这里少一句话，好过多一句假话。**缺的模板已提给流 0。**
             */
            !access.user_keys_intact ? (
              <T id="host.access.keys.yours_none" />
            ) : access.user_key_count ? (
              <T id="host.access.keys.yours" vars={{ count: access.user_key_count }} />
            ) : null
          }
          tone={statusTone(access, usable(access))}
          status={
            !access.live ? (
              <T id="host.access.yours.status.unknown" />
            ) : usable(access) ? (
              <T id="host.access.yours.status.usable" />
            ) : (
              // **红的，而且把后果说出来**：光说「这把不能用了」他不会当回事，
              // 而底线断了的那一刻正是这一档
              <T id="host.access.yours.status.unusable" />
            )
          }
          when={
            access.checked_at ? (
              <T id="host.access.yours.checked_at" vars={{ when: ago(access.checked_at) }} />
            ) : (
              // 与 status.unknown 分开：那条是「这一次没验」，这条是「从来没验过」——
              // 一次都没验过多半是接进来那天就没对上，要查的地方完全不同
              <T id="host.access.yours.never_checked" />
            )
          }
          usedBy={<T id="host.access.yours.used_by" />}
          extra={
            /* 他给的那份凭证。**撤掉了也要说**——不说的话这一行直接消失，
               而一个消失的东西和一个从来没有过的东西在界面上长得一模一样 */
            access.user_credential?.revoked ? (
              <T id="host.access.credential.revoked" />
            ) : access.user_credential ? (
              <T id="host.access.credential.kept" />
            ) : null
          }
        />

        <Column
          headline={<T id="host.access.keys.ours" />}
          // neutral 不是 ok：**平台有钥匙不是一件值得庆祝的事，它只是我们的东西**
          tone={access.platform_key_installed ? 'neutral' : 'warn'}
          status={
            !access.live ? (
              <T id="host.access.ours.status.unknown" />
            ) : access.platform_key_installed ? (
              <T id="host.access.ours.status.usable" />
            ) : (
              // **琥珀不是红**：这一档他没有被关在外面，只是我们瘸了
              <T id="host.access.ours.status.unusable" />
            )
          }
          when={
            access.checked_at ? (
              <T id="host.access.ours.checked_at" vars={{ when: ago(access.checked_at) }} />
            ) : null
          }
          usedBy={<T id="host.access.ours.used_by" />}
        />
      </div>
    </Card>
  )
}

/**
 * 他那把还灵不灵。契约里没有这一格，用 `user_keys_intact`——
 * 它的定义就是「他自己的入口还在不在」。
 */
function usable(access: Schemas['HostAccessMethod']): boolean {
  return access.user_keys_intact
}

function statusTone(
  access: Schemas['HostAccessMethod'],
  ok: boolean,
): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (!access.live) return 'warn'
  return ok ? 'ok' : 'danger'
}

function Column({
  headline,
  tone,
  status,
  when,
  usedBy,
  extra,
}: {
  headline: React.ReactNode
  tone: 'ok' | 'warn' | 'danger' | 'neutral'
  status: React.ReactNode
  when: React.ReactNode
  usedBy: React.ReactNode
  extra?: React.ReactNode
}) {
  return (
    <div className="rounded-npc-card-sm border border-npc-border-soft p-npc-grid-sm">
      <div className="mb-npc-gap-sm flex items-start gap-npc-gap-sm">
        <span className="mt-[5px]">
          <StatusDot tone={tone} />
        </span>
        {/* 标题可以没有（密码那一档没有合适的模板，见上面）。
            **没有就不画那个 <p>**，不留一个空段落撑着行高 */}
        {headline ? <p className="m-0 text-npc-body-sm leading-[1.7]">{headline}</p> : null}
      </div>

      <p className="m-0 text-npc-caption leading-[1.7] text-npc-ink-medium">{status}</p>
      {when ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">{when}</p>
      ) : null}
      <p className="m-0 mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-ink-soft">{usedBy}</p>
      {extra ? (
        <p className="m-0 mt-npc-gap-sm border-t border-npc-border-softer pt-npc-gap-sm text-npc-caption leading-[1.7] text-npc-ink-soft">
          {extra}
        </p>
      ) : null}
    </div>
  )
}
